/**
 * Centralized analytics event helpers.
 *
 * Usage:  import { track } from "@/lib/analytics";
 *         track.ctaClick("Book check-up", "hero");
 *
 * All functions are no-ops on the server. PostHog initializes in every
 * environment; capture is gated by a `before_send` filter in `lib/posthog.ts`
 * that drops non-production events (AHD-6306) — `$snapshot` events and the
 * system-check page are the only carve-outs.
 */
import { posthog } from "@/lib/posthog"
import { stripPiiForPostHog } from "@ahead-health/core/analytics/posthogPii"

function capture(event: string, properties?: Record<string, unknown>) {
  if (typeof window === "undefined") return
  // Identity fields are stripped here rather than at each call site, so a new
  // `track.*` helper can't reintroduce the AHD-9528 leak. `lib/posthog.ts`'s
  // `before_send` strips again as the last line of defence.
  posthog.capture(event, stripPiiForPostHog(properties))
}

/** Where a search happens. Shared by `searchQuery` and the suggestion events. */
export type SearchSurface =
  | "modal"
  | "navbar-inline"
  | "page"
  | "faq"
  | "insights"
  | "biomarkers"
  | "diseases"
  | "explorer-biomarkers"
  | "explorer-diseases"

export const track = {
  // ── Navigation ──
  pageView(url: string, referrer?: string) {
    capture("$pageview", { $current_url: url, $referrer: referrer })
  },

  // ── 404 / not-found ──
  //
  // Not exposed here — the not-found surfaces don't client-hydrate under
  // Next 16 cacheComponents (useEffect never fires on direct landings).
  // Instead, see `components/analytics/not-found-beacon.tsx`, which
  // emits the `page_not_found` event + a Sentry warning via an inline
  // `<script>` that runs during HTML parse.

  // ── CTA clicks (buttons, links that drive conversion) ──
  ctaClick(label: string, location: string, href?: string) {
    capture("cta_click", { label, location, href })
  },

  // ── Booking funnel (mirrors Framer events) ──
  bookingStep(step: string, properties?: Record<string, unknown>) {
    capture("booking_step", { step, ...properties })
  },

  // ── Package selection ──
  packageDialogView(packageName: string) {
    capture("package_dialog_view", { package: packageName })
  },
  packageDialogSelect(packageName: string) {
    capture("package_dialog_select", { package: packageName })
  },

  // ── Insurance form ──
  insuranceFormStarted() {
    capture("insurance_form_started")
  },
  insuranceFormInsurerSelected(insurer: string) {
    capture("insurance_form_insurer_selected", { insurer })
  },
  insuranceFormServiceSelected(service: string) {
    capture("insurance_form_service_selected", { service })
  },
  insuranceFormSubmitted(insurer: string, service: string) {
    capture("insurance_form_submitted", { insurer, service })
  },

  // ── Waitlist ──
  waitlistModalOpened() {
    capture("waitlist.modal_opened")
  },
  waitlistSignup(_email: string) {
    // Don't send email to PostHog — just the event
    capture("waitlist.signup")
  },
  waitlistEnriched() {
    capture("waitlist.enriched")
  },
  waitlistSkipped() {
    capture("waitlist.skipped")
  },

  // ── Brand logo asset menu (right-click / select-copy the site logo) ──
  logoCopied(format: "svg", color: string) {
    capture("logo_asset_copied", { format, color })
  },
  logoDownloaded(format: "svg" | "png", color: string) {
    capture("logo_asset_downloaded", { format, color })
  },

  // ── Gift cards ──
  giftCardSelect(amount: number, currency: string) {
    capture("gift_card.select", { amount, currency })
  },
  giftCardCheckoutStart(amount: number, currency: string) {
    capture("gift_card.checkout_start", { amount, currency })
  },

  // ── Locale suggestion ──
  localeSuggestionShown(geo: string, currentCountry: string) {
    capture("locale_suggestion.shown", { geo, current_country: currentCountry })
  },
  localeSuggestionClicked(targetCountry: string, targetLocale: string) {
    capture("locale_suggestion.clicked", {
      target_country: targetCountry,
      target_locale: targetLocale,
    })
  },
  localeSuggestionDismissed(geo: string) {
    capture("locale_suggestion.dismissed", { geo })
  },

  // ── Locale picker (manual market/language switch from header or footer) ──
  // Distinct from `locale_suggestion.*` (geo-modal). Fired when the user
  // actively picks a different country/language from the picker UI. Surface
  // tells us which placement got the click (navbar-desktop, navbar-mobile,
  // or the mobile-only footer fallback).
  localeChanged(props: {
    surface: "navbar-desktop" | "navbar-mobile" | "footer" | "quiz-chrome"
    from_country: string
    from_locale: string
    to_country: string
    to_locale: string
    page_path: string
  }) {
    capture("locale_changed", props)
  },

  // ── Health calculators (/tools) ──
  //
  // One event per click on Calculate. Every calculator page tells the reader
  // that nothing they enter is sent to us, so this carries which calculator
  // ran and whether it produced a score, never an entered value or the score
  // itself. `reason` is the validation key the page answered with (e.g.
  // `ageOutOfRange`, `cholMmolValue`), which says which check tripped without
  // saying what was typed.
  calculatorResult(props: {
    calculator: "bmi" | "bmr" | "score2" | "fib4" | "smaf5" | "mafld"
    outcome: "result" | "rejected"
    reason?: string
  }) {
    capture("calculator_result", props)
  },

  // ── Content engagement ──
  articleView(slug: string, title: string, category: string) {
    capture("article_view", { slug, title, category })
  },
  // `slug` already identifies the author page; the display name was redundant
  // and is on the PostHog denylist (AHD-9528), so it would have been stripped
  // at capture time anyway. This helper has no callers today.
  authorView(slug: string) {
    capture("author_view", { slug })
  },
  searchQuery(props: {
    query: string
    result_count: number
    /** Where the search input lives. `modal` + `page` are the global
     *  search; everything else is a page-scoped search. `biomarkers` and
     *  `diseases` are the /biomarkers and /what-we-detect pages.
     *  `explorer-biomarkers` and `explorer-diseases` are the shared
     *  BiomarkerDiseaseExplorer component (used on /tests/[slug],
     *  /membership, and various landing pages), keyed by its active tab. */
    surface: SearchSurface
    /** Only meaningful for global search (`surface` ∈ {modal, page}).
     *  Tells us *how* the user reached the global search. Omitted for
     *  page-scoped searches. */
    source?: "navbar-desktop" | "navbar-mobile" | "shortcut" | "page"
    locale?: string
    zero_results?: boolean
  }) {
    capture("search_query", props)
  },
  /** Fired when the zero-result fallback surfaces ≥1 cross-catalog match. */
  searchSuggestionShown(props: {
    query: string
    surface: SearchSurface
    match_count: number
    type: "biomarkers" | "diseases"
    locale?: string
  }) {
    capture("search_suggestion_shown", props)
  },
  /** Fired when the zero-result KB fallback surfaces ≥1 result ("From our pages"). */
  searchKbFallbackShown(props: {
    query: string
    surface: SearchSurface
    result_count: number
    locale?: string
  }) {
    capture("search_kb_fallback_shown", props)
  },
  /** Fired when the user clicks a KB fallback result. */
  searchKbFallbackClicked(props: {
    query: string
    surface: SearchSurface
    url: string
    rank: number
    locale?: string
  }) {
    capture("search_kb_fallback_clicked", props)
  },
  /** Fired when the user engages with the zero-result cross-catalog suggestion. */
  searchSuggestionClicked(props: {
    query: string
    surface: SearchSurface
    /** The primary suggested test. For `clear-scope` this is the test the user
     *  was offered but bypassed in favour of broadening the search in place. */
    target_test: string
    /** `test` = clicked a test link; `detail` = clicked the "+N more" / item
     *  link; `clear-scope` = dropped the active package filter to search the
     *  whole catalog in place (only on the package-scoped pages). */
    target_type: "test" | "detail" | "clear-scope"
  }) {
    capture("search_suggestion_clicked", props)
  },
  searchOpened(props: {
    source: "navbar-desktop" | "navbar-mobile" | "shortcut" | "page"
    country: string
    locale: string
  }) {
    capture("search_opened", props)
  },
  searchClosed(props: {
    country: string
    locale: string
    last_query: string | null
    result_count: number | null
  }) {
    capture("search_closed", props)
  },
  searchResultClicked(props: {
    query: string
    url: string
    rank: number
    type: string
    surface: "modal" | "navbar-inline" | "page"
    country: string
    locale: string
  }) {
    capture("search_result_clicked", props)
  },
  searchPromptClicked(props: {
    prompt_id: string
    surface: "modal" | "page"
    country: string
    locale: string
  }) {
    capture("search_prompt_clicked", props)
  },
  searchPopularClicked(props: {
    popular_id: string
    rank: number
    surface: "modal" | "page"
    country: string
    locale: string
  }) {
    capture("search_popular_clicked", props)
  },
  faqExpand(question: string, page: string) {
    capture("faq_expand", { question, page })
  },

  // ── AI lane escalation (human escape hatch) ──
  /** Fired when the user takes the human-escape-hatch on an AI answer:
   *  opens the Chatwoot full launcher. */
  aiAnswerEscalated({
    surface,
    scope,
    target,
  }: {
    surface: string
    scope: string
    target: "chatwoot"
  }) {
    capture("ai_answer_escalated", { surface, scope, target })
  },

  // ── AI lane offer / request ──
  /** Fired once per distinct query when the lane surfaces an intent-driven
   *  offer (the "ask Ella" CTA or the nav row) for a controlled query. */
  aiAnswerOffered({
    surface,
    scope,
    intent,
    query_length,
  }: {
    surface: string
    scope: string
    intent: "nav" | "question" | "list"
    query_length: number
  }) {
    capture("ai_answer_offered", { surface, scope, intent, query_length })
  },
  /** Fired when the user actually submits a message into the AI lane. */
  aiAnswerRequested({
    surface,
    scope,
    intent,
    query_length,
  }: {
    surface: string
    scope: string
    intent: "nav" | "question" | "list"
    query_length: number
  }) {
    capture("ai_answer_requested", { surface, scope, intent, query_length })
  },

  // ── Carousel / interactive elements ──
  carouselScroll(section: string, direction: "left" | "right") {
    capture("carousel_scroll", { section, direction })
  },

  // ── Test detail pages ──
  viewItem(item: string, category?: string) {
    capture("view_item", { item, category })
  },
  diseaseCategorySelect(category: string) {
    capture("disease_category_select", { category })
  },
  diseaseSearch(query: string, resultCount: number) {
    capture("disease_search", { query, result_count: resultCount })
  },

  // ── Quiz funnel ──
  quizStepViewed(stepId: string, stepIndex: number, totalSteps: number) {
    capture("quiz_step_viewed", {
      step_id: stepId,
      step_index: stepIndex,
      total_steps: totalSteps,
    })
  },

  quizStepAnswered(
    stepId: string,
    answer: Record<string, unknown> | string | number | boolean,
    stepIndex: number
  ) {
    capture("quiz_step_answered", {
      step_id: stepId,
      answer,
      step_index: stepIndex,
    })
  },

  quizCompleted(props: {
    recommendation_base: string
    recommendation_addons: string[]
    age?: number
    gender?: string
    insurer_id?: string
    /** Absent when the market ships no price for the recommended package. */
    total_price_chf?: number
  }) {
    capture("quiz_completed", props)
  },

  proFormaRequested(props: Record<string, unknown>) {
    capture("pro_forma_requested", props)
  },

  quizAbandoned(stepId: string, stepIndex: number) {
    capture("quiz_abandoned", { step_id: stepId, step_index: stepIndex })
  },

  // ── Exit intent ──
  exitIntentShown(variant: string) {
    capture("exit_intent.shown", { variant })
  },
  exitIntentDismissed(variant: string, reason: string) {
    capture("exit_intent.dismissed", { variant, reason })
  },
  exitIntentCtaClicked(variant: string, cta: string) {
    capture("exit_intent.cta_clicked", { variant, cta })
  },

  // ── External link clicks ──
  externalLinkClick(href: string, label: string) {
    capture("external_link_click", { href, label })
  },

  // ── Chat interactions ──
  chat: {
    opened(props: {
      scope: string
      country: string
      locale: string
      page_slug: string
      entry_point: "inline" | "launcher"
      conversation_id?: number
    }) {
      capture("chat_opened", props)
    },
    starterPromptSelected(props: {
      scope: string
      prompt_key: string
      prompt_index: number
      prompt_variant?: string
    }) {
      capture("chat_starter_prompt_selected", props)
    },
    /** Follow-up suggestion chips shown beneath a bot answer. `source`
     *  distinguishes LLM-generated chips from the static medical-safe set;
     *  `count` is the impression denominator for follow-up CTR. */
    followupSuggested(props: {
      scope: string
      conversation_id?: number
      turn_index: number
      source: "generated" | "static"
      /** When `source === "static"`, which gate routed this turn (glue's
       *  `StaticReason`) — lets us see which layer over-blocks. */
      static_reason?: string
      count: number
    }) {
      capture("chat_followup_suggested", props)
    },
    /** A visitor tapped a follow-up chip (sent as their next message). */
    followupClicked(props: {
      scope: string
      conversation_id?: number
      turn_index: number
      rank: number
      prompt_key: string
      source: "generated" | "static"
    }) {
      capture("chat_followup_clicked", props)
    },
    messageSent(props: {
      scope: string
      turn_index: number
      // Optional: the opening turn fires before the conversation id is known on
      // the rare no-conversation path. Omit it rather than report the 0 sentinel
      // (which otherwise pools unrelated visitors into one fake conversation).
      conversation_id?: number
      message_length: number
    }) {
      capture("chat_message_sent", props)
    },
    botResponseReceived(props: {
      scope: string
      turn_index: number
      conversation_id: number
      retrieval_confidence_bucket: "high" | "med" | "low"
      latency_ms: number
    }) {
      capture("chat_bot_response_received", props)
    },
    handoffRequested(props: {
      scope: string
      conversation_id: number
      reason:
        | "user_requested"
        | "low_confidence"
        | "out_of_scope"
        | "clinical_judgment"
        | "crisis"
      turn_index: number
    }) {
      capture("chat_handoff_requested", props)
    },
    humanReplyReceived(props: {
      scope: string
      conversation_id: number
      wait_ms: number
    }) {
      capture("chat_human_reply_received", props)
    },
    ctaClicked(props: {
      scope: string
      conversation_id: number
      cta_label: string
      cta_target_url: string
    }) {
      capture("chat_cta_clicked", props)
    },
    closed(props: {
      scope: string
      conversation_id?: number
      turn_count: number
      handed_off: boolean
    }) {
      capture("chat_closed", props)
    },
    teaserShown(props: {
      scope: string
      surface: "desktop" | "mobile"
      prompt_variant?: string
    }) {
      capture("chat_teaser_shown", props)
    },
    teaserDismissed(props: {
      scope: string
      surface: "desktop" | "mobile"
      prompt_variant?: string
    }) {
      capture("chat_teaser_dismissed", props)
    },
    advisorShown(props: { scope: string; prompt_scope: string }) {
      capture("chat_advisor_shown", props)
    },
    advisorFreetextSubmitted(props: {
      scope: string
      prompt_scope: string
      char_len: number
    }) {
      capture("chat_advisor_freetext_submitted", props)
    },
  },
}
